/**
 * Cross-Org Config Copy API — Org Architecture Phase D ("Apply to organizations").
 *
 * Mirrors apps/chaos-gateway/src/config-copy.ts. Owner-gated (account owner).
 * Flag-gated server-side (CROSS_ORG_COPY_ENABLED, default OFF) — every route 404s
 * when the flag is OFF, so the UI section must degrade gracefully on 404.
 *
 * Wire shape from @chaos/types (orgs.ts Phase-D schemas).
 */
import type {
  ConfigCopyJob,
  ConfigCopyObjectType,
  ConfigCopyConflictPolicy,
  ConfigCopyResult,
} from '@chaos/types';

import { apiJson } from './client';

export interface ConfigCopyRequest {
  objectType: ConfigCopyObjectType;
  sourceObjectId: string;
  sourceOrgId: string;
  targetOrgIds: string[];
  conflictPolicy?: ConfigCopyConflictPolicy;
}

export interface ConfigCopyResponse {
  jobId: string | null;
  objectType: ConfigCopyObjectType;
  conflictPolicy: ConfigCopyConflictPolicy;
  summary: { created: number; skipped: number; errored: number };
  results: ConfigCopyResult[];
}

interface JobsListResponse {
  jobs: ConfigCopyJob[];
  count: number;
}

export const configCopyApi = {
  /** "Apply to organizations": copy a source config object into target orgs. */
  apply(req: ConfigCopyRequest): Promise<ConfigCopyResponse> {
    return apiJson<ConfigCopyResponse>('/account/config-copy', {
      method: 'POST',
      json: req,
    });
  },

  /** Copy history (audit) for the account. */
  listJobs(): Promise<ConfigCopyJob[]> {
    return apiJson<JobsListResponse>('/account/config-copy/jobs').then(
      (r) => r.jobs,
    );
  },
};

export const configCopyKeys = {
  all: ['config-copy'] as const,
  jobs: () => [...configCopyKeys.all, 'jobs'] as const,
};
